import * as THREE from 'three';
import { OrbConnectionManager } from '../services/connections/OrbConnectionManager';
import { DEFAULT_PRESENTATION_ARROW_PROFILE, getPresentationProfileName, } from '../services/connections/ConnectionPresentation';
function resolveBasis(normal) {
    const n = normal.clone().normalize();
    const helper = Math.abs(n.x) > 0.8 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(n, helper).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    return { u, v };
}
function computeRingRadius(items, minRadius, minSeparation) {
    if (items.length <= 1) {
        const single = items[0];
        const fallback = single ? single.radius * 1.6 + minSeparation : 0;
        return Math.max(minRadius, fallback);
    }
    const n = items.length;
    const sinTerm = Math.sin(Math.PI / n);
    const minGap = minSeparation > 0 ? minSeparation : 0;
    const maxItemRadius = items.reduce((acc, item) => Math.max(acc, item.radius), 0);
    let required = 0;
    for (let i = 0; i < n; i++) {
        const current = items[i];
        const next = items[(i + 1) % n];
        const pairGap = Math.max(minGap, Math.max(current.radius, next.radius));
        const chord = current.radius + next.radius + pairGap;
        required = Math.max(required, chord);
    }
    const base = sinTerm === 0 ? required : required / (2 * sinTerm);
    const padding = maxItemRadius * 1.1 + minGap * 0.5;
    return Math.max(minRadius, base, padding);
}
export function computeRingLayout(items, options = {}) {
    var _a, _b, _c, _d, _e;
    const centre = options.centre ? options.centre.clone() : new THREE.Vector3();
    const normal = options.normal ? options.normal.clone().normalize() : new THREE.Vector3(0, 1, 0);
    const planeAxis = options.planeAxis ? options.planeAxis.clone().normalize() : new THREE.Vector3(0, 1, 0);
    const minRadius = (_a = options.minRadius) !== null && _a !== void 0 ? _a : 0;
    const minSeparation = (_b = options.minSeparation) !== null && _b !== void 0 ? _b : 0;
    const startAngle = (_c = options.startAngle) !== null && _c !== void 0 ? _c : 0;
    const baseAngularVelocity = (_d = options.angularVelocity) !== null && _d !== void 0 ? _d : 0.35;
    const direction = options.clockwise === false ? -1 : 1;
    const adjustedCentre = centre.clone();
    if (typeof options.fixedAxisCoordinate === 'number') {
        const projection = adjustedCentre.dot(planeAxis);
        adjustedCentre.add(planeAxis.clone().multiplyScalar(options.fixedAxisCoordinate - projection));
    }
    if (typeof options.planeOffset === 'number' && options.planeOffset !== 0) {
        adjustedCentre.add(planeAxis.clone().multiplyScalar(options.planeOffset));
    }
    const radius = (_e = options.radius) !== null && _e !== void 0 ? _e : computeRingRadius(items, minRadius, minSeparation);
    const { u, v } = resolveBasis(normal);
    const animationDefaults = {
        angularVelocity: baseAngularVelocity * direction,
        baseAngle: startAngle,
    };
    if (items.length === 0) {
        return {
            centre: adjustedCentre,
            radius,
            normal,
            axis: planeAxis,
            basis: { u: u.clone(), v: v.clone() },
            animation: animationDefaults,
            placements: [],
        };
    }
    const placements = [];
    const weights = items.map(item => { var _a; return Math.max(0, (_a = item.weight) !== null && _a !== void 0 ? _a : 1); });
    const totalWeight = weights.reduce((acc, value) => acc + value, 0);
    const useWeighted = options.stepMode === 'weighted' || weights.some(w => { var _a; return Math.abs(w - ((_a = weights[0]) !== null && _a !== void 0 ? _a : 1)) > 1e-6; });
    const evenStep = (Math.PI * 2) / Math.max(items.length, 1);
    const weightedStep = totalWeight > 0 ? (Math.PI * 2) / totalWeight : evenStep;
    let weightAccumulator = 0;
    items.forEach((item, index) => {
        var _a, _b, _c, _d, _e;
        const weight = weights[index] || 1;
        let baseAngle;
        if (typeof item.angle === 'number') {
            baseAngle = item.angle;
        }
        else if (useWeighted && totalWeight > 0) {
            const midpoint = weightAccumulator + weight * 0.5;
            baseAngle = startAngle + direction * weightedStep * midpoint;
        }
        else {
            baseAngle = startAngle + direction * evenStep * index;
        }
        weightAccumulator += weight;
        const finalAngle = baseAngle + ((_a = item.angularOffset) !== null && _a !== void 0 ? _a : 0);
        const orbitRadius = (_b = item.orbitRadius) !== null && _b !== void 0 ? _b : radius;
        const cos = Math.cos(finalAngle);
        const sin = Math.sin(finalAngle);
        const planarOffset = u.clone().multiplyScalar(cos * orbitRadius).add(v.clone().multiplyScalar(sin * orbitRadius));
        const position = adjustedCentre.clone().add(planarOffset);
        const normalOffset = (_c = item.offsetAlongNormal) !== null && _c !== void 0 ? _c : 0;
        if (normalOffset !== 0) {
            position.add(normal.clone().multiplyScalar(normalOffset));
        }
        const axisOffset = (_d = item.axisOffset) !== null && _d !== void 0 ? _d : 0;
        if (axisOffset !== 0) {
            position.add(planeAxis.clone().multiplyScalar(axisOffset));
        }
        let absoluteAxisCoordinate;
        if (typeof item.absoluteAxisCoordinate === 'number') {
            absoluteAxisCoordinate = item.absoluteAxisCoordinate;
            const projection = position.dot(planeAxis);
            position.add(planeAxis.clone().multiplyScalar(absoluteAxisCoordinate - projection));
        }
        let resolvedNormal = normal;
        if (item.normal) {
            resolvedNormal = item.normal.clone().normalize();
        }
        const { u: childU, v: childV } = resolveBasis(resolvedNormal);
        const baseVelocity = (_e = item.angularVelocity) !== null && _e !== void 0 ? _e : baseAngularVelocity;
        const itemDirection = item.clockwise === false ? -1 : item.clockwise === true ? 1 : direction;
        const angularVelocity = baseVelocity * itemDirection;
        placements.push({
            id: item.id,
            angle: finalAngle,
            position,
            radius: orbitRadius,
            normal: resolvedNormal,
            axis: planeAxis.clone(),
            basis: { u: childU, v: childV },
            orbitCentre: adjustedCentre.clone(),
            normalOffset,
            axisOffset,
            absoluteAxisCoordinate,
            animation: { angularVelocity, baseAngle: finalAngle },
            item,
        });
    });
    return {
        centre: adjustedCentre,
        radius,
        normal,
        axis: planeAxis,
        basis: { u: u.clone(), v: v.clone() },
        animation: animationDefaults,
        placements,
    };
}
function cloneLayoutOptionVectors(source, target) {
    if (!source)
        return;
    if (source.centre)
        target.centre = source.centre.clone();
    if (source.normal)
        target.normal = source.normal.clone();
    if (source.planeAxis)
        target.planeAxis = source.planeAxis.clone();
}
function mergeLayoutOptions(base, override) {
    if (!base && !override)
        return undefined;
    const merged = {};
    if (base) {
        Object.assign(merged, base);
        cloneLayoutOptionVectors(base, merged);
    }
    if (override) {
        Object.assign(merged, override);
        cloneLayoutOptionVectors(override, merged);
    }
    return merged;
}
function duplicateRingItem(item) {
    const clone = {
        id: item.id,
        radius: item.radius,
        weight: item.weight,
        angularOffset: item.angularOffset,
        angle: item.angle,
        offsetAlongNormal: item.offsetAlongNormal,
        axisOffset: item.axisOffset,
        absoluteAxisCoordinate: item.absoluteAxisCoordinate,
        angularVelocity: item.angularVelocity,
        clockwise: item.clockwise,
        orbitRadius: item.orbitRadius,
        metadata: item.metadata ? Object.assign({}, item.metadata) : undefined,
    };
    if (item.normal)
        clone.normal = item.normal.clone();
    if (item.layoutOptions)
        clone.layoutOptions = mergeLayoutOptions(item.layoutOptions, undefined);
    if (item.children)
        clone.children = item.children.map(child => duplicateRingItem(child));
    return clone;
}
export function computeRingHierarchy(items, options = {}) {
    const layout = computeRingLayout(items, options);
    const placements = layout.placements.map((placement, index) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const source = items[index];
        let childLayout;
        if ((source === null || source === void 0 ? void 0 : source.children) && source.children.length > 0) {
            const inherited = (_a = mergeLayoutOptions(options, undefined)) !== null && _a !== void 0 ? _a : {};
            delete inherited.centre;
            const override = mergeLayoutOptions(source.layoutOptions, undefined);
            const mergedOptions = (_b = mergeLayoutOptions(inherited, override)) !== null && _b !== void 0 ? _b : {};
            const centreCandidate = (_c = override === null || override === void 0 ? void 0 : override.centre) !== null && _c !== void 0 ? _c : placement.position;
            mergedOptions.centre = centreCandidate.clone();
            const normalCandidate = (_e = (_d = override === null || override === void 0 ? void 0 : override.normal) !== null && _d !== void 0 ? _d : source.normal) !== null && _e !== void 0 ? _e : placement.normal;
            mergedOptions.normal = normalCandidate.clone();
            const axisCandidate = (_g = (_f = override === null || override === void 0 ? void 0 : override.planeAxis) !== null && _f !== void 0 ? _f : options.planeAxis) !== null && _g !== void 0 ? _g : placement.axis;
            mergedOptions.planeAxis = axisCandidate.clone();
            const childItems = source.children.map(child => duplicateRingItem(child));
            childLayout = computeRingHierarchy(childItems, mergedOptions);
        }
        return Object.assign(Object.assign({}, placement), { position: placement.position.clone(), normal: placement.normal.clone(), axis: placement.axis.clone(), basis: { u: placement.basis.u.clone(), v: placement.basis.v.clone() }, orbitCentre: placement.orbitCentre.clone(), animation: Object.assign({}, placement.animation), childLayout });
    });
    return {
        centre: layout.centre.clone(),
        radius: layout.radius,
        normal: layout.normal.clone(),
        axis: layout.axis.clone(),
        basis: { u: layout.basis.u.clone(), v: layout.basis.v.clone() },
        animation: Object.assign({}, layout.animation),
        placements,
    };
}
class RingOrbitAnimationRegistry {
    constructor() {
        this.animators = new Set();
    }
    register(animator) {
        this.animators.add(animator);
    }
    unregister(animator) {
        this.animators.delete(animator);
    }
    update(deltaSeconds, activeScene) {
        for (const animator of this.animators) {
            animator.step(deltaSeconds, activeScene);
        }
    }
}
export const ringOrbitAnimationRegistry = new RingOrbitAnimationRegistry();
export class RingOrbitAnimator {
    constructor(autoRegister = true) {
        this.bindings = [];
        this.active = true;
        if (autoRegister) {
            ringOrbitAnimationRegistry.register(this);
        }
    }
    add(binding) {
        if (!this.active)
            return;
        this.bindings.push({
            object: binding.object,
            placement: binding.placement,
            centreProvider: binding.centreProvider,
            phase: binding.placement.animation.baseAngle,
        });
    }
    update(binding, resetPhase = false) {
        if (!this.active)
            return;
        const existing = this.bindings.find(entry => entry.object === binding.object);
        if (!existing) {
            this.add(binding);
            return;
        }
        existing.placement = binding.placement;
        existing.centreProvider = binding.centreProvider;
        if (resetPhase) {
            existing.phase = binding.placement.animation.baseAngle;
        }
    }
    remove(object) {
        if (!this.active || this.bindings.length === 0)
            return;
        this.bindings = this.bindings.filter(binding => binding.object !== object);
    }
    clear() {
        this.bindings = [];
    }
    dispose() {
        if (!this.active)
            return;
        this.active = false;
        ringOrbitAnimationRegistry.unregister(this);
        this.bindings = [];
    }
    step(deltaSeconds, activeScene) {
        var _a, _b;
        if (!this.active || this.bindings.length === 0)
            return;
        for (const binding of this.bindings) {
            if (activeScene && !isObjectInScene(binding.object, activeScene)) {
                continue;
            }
            const { placement } = binding;
            binding.phase += placement.animation.angularVelocity * deltaSeconds;
            const angle = binding.phase;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const centre = (_b = (_a = binding.centreProvider) === null || _a === void 0 ? void 0 : _a.call(binding)) !== null && _b !== void 0 ? _b : placement.orbitCentre.clone();
            const offset = placement.basis.u.clone().multiplyScalar(cos * placement.radius).add(placement.basis.v.clone().multiplyScalar(sin * placement.radius));
            const position = centre.clone().add(offset);
            if (placement.normalOffset !== 0) {
                position.add(placement.normal.clone().multiplyScalar(placement.normalOffset));
            }
            if (placement.axisOffset !== 0) {
                position.add(placement.axis.clone().multiplyScalar(placement.axisOffset));
            }
            if (typeof placement.absoluteAxisCoordinate === 'number') {
                const projection = position.dot(placement.axis);
                position.add(placement.axis.clone().multiplyScalar(placement.absoluteAxisCoordinate - projection));
            }
            binding.object.position.copy(position);
        }
    }
}
function isObjectInScene(object, scene) {
    let current = object;
    while (current && current.parent) {
        current = current.parent;
    }
    return current === scene;
}
const ringChildRegistry = new Map();
const ringPresentationRegistry = new Map();
function ensureParentPresentationState(parentId) {
    let entry = ringPresentationRegistry.get(parentId);
    if (!entry) {
        entry = { mode: 'orbit', childModes: new Map() };
        ringPresentationRegistry.set(parentId, entry);
    }
    return entry;
}
export function getRingParentPresentationMode(parent) {
    var _a;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    if (!parentId)
        return 'orbit';
    return ensureParentPresentationState(parentId).mode;
}
export function setRingParentPresentationMode(parent, mode) {
    var _a;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    if (!parentId)
        return;
    const entry = ensureParentPresentationState(parentId);
    entry.mode = mode;
}
export function getRingChildPresentationMode(parent, child) {
    var _a, _b, _c;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    const childId = (_b = child.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!parentId || !childId)
        return undefined;
    const entry = ensureParentPresentationState(parentId);
    return (_c = entry.childModes.get(childId)) !== null && _c !== void 0 ? _c : entry.mode;
}
export function setRingChildPresentationMode(parent, child, mode) {
    var _a, _b;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    const childId = (_b = child.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!parentId || !childId)
        return;
    const entry = ensureParentPresentationState(parentId);
    entry.childModes.set(childId, mode);
}
function clearRingChildPresentationMode(parent, child) {
    var _a, _b;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    const childId = (_b = child.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!parentId || !childId)
        return;
    const entry = ringPresentationRegistry.get(parentId);
    entry === null || entry === void 0 ? void 0 : entry.childModes.delete(childId);
}
function clearRingPresentationModes(parent) {
    var _a;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    if (!parentId)
        return;
    ringPresentationRegistry.delete(parentId);
}
export function registerRingChild(record) {
    var _a, _b, _c;
    const parentId = (_a = record.parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    const childId = (_b = record.child.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!parentId || !childId)
        return;
    let childMap = ringChildRegistry.get(parentId);
    if (!childMap) {
        childMap = new Map();
        ringChildRegistry.set(parentId, childMap);
    }
    childMap.set(childId, record);
    const manager = OrbConnectionManager.getInstance();
    const mode = (_c = getRingChildPresentationMode(record.parent, record.child)) !== null && _c !== void 0 ? _c : 'orbit';
    setRingChildPresentationMode(record.parent, record.child, mode);
    manager.setRelationshipState({
        a: record.parent,
        b: record.child,
        mode,
        profileName: getPresentationProfileName(record.parent, record.child),
        profile: DEFAULT_PRESENTATION_ARROW_PROFILE,
    });
}
export function unregisterRingChild(parent, child) {
    var _a, _b;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    const childId = (_b = child.group) === null || _b === void 0 ? void 0 : _b.uuid;
    if (!parentId || !childId)
        return;
    const childMap = ringChildRegistry.get(parentId);
    if (!childMap)
        return;
    childMap.delete(childId);
    if (childMap.size === 0) {
        ringChildRegistry.delete(parentId);
    }
    clearRingChildPresentationMode(parent, child);
}
export function clearRegisteredRingChildren(parent) {
    var _a;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    if (!parentId)
        return;
    ringChildRegistry.delete(parentId);
    clearRingPresentationModes(parent);
}
export function getRegisteredRingChildren(parent) {
    var _a;
    const parentId = (_a = parent.group) === null || _a === void 0 ? void 0 : _a.uuid;
    if (!parentId)
        return [];
    const childMap = ringChildRegistry.get(parentId);
    if (!childMap)
        return [];
    return Array.from(childMap.values());
}
